import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import {
  ClipboardList, Plus, BarChart3, Send, CheckCircle2, Eye
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const Surveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [selectedResults, setSelectedResults] = useState(null);
  const [newSurvey, setNewSurvey] = useState({ title: "", description: "", survey_type: "clima", questions: [] });
  const [questionInput, setQuestionInput] = useState("");

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/surveys`, { headers: headers(), withCredentials: true });
      setSurveys(res.data || []);
    } catch { toast.error("Error al cargar encuestas"); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newSurvey.title) { toast.error("Título requerido"); return; }
    try {
      await axios.post(`${API}/surveys`, newSurvey, { headers: headers(), withCredentials: true });
      toast.success("Encuesta creada");
      setShowCreateDialog(false);
      setNewSurvey({ title: "", description: "", survey_type: "clima", questions: [] });
      fetchData();
    } catch { toast.error("Error al crear encuesta"); }
  };

  const handlePublish = async (surveyId) => {
    try {
      await axios.put(`${API}/surveys/${surveyId}/publish`, {}, { headers: headers(), withCredentials: true });
      toast.success("Encuesta publicada");
      fetchData();
    } catch { toast.error("Error al publicar"); }
  };

  const handleViewResults = async (surveyId) => {
    try {
      const res = await axios.get(`${API}/surveys/${surveyId}/results`, { headers: headers(), withCredentials: true });
      setSelectedResults(res.data);
      setShowResultsDialog(true);
    } catch { toast.error("Error al cargar resultados"); }
  };

  const addQuestion = () => {
    if (!questionInput.trim()) return;
    setNewSurvey(prev => ({ ...prev, questions: [...prev.questions, { id: `q${prev.questions.length + 1}`, text: questionInput.trim(), type: "scale" }] }));
    setQuestionInput("");
  };

  if (loading) return <Layout title="Encuestas"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Encuestas de Clima" subtitle="Mide la satisfacción y el clima organizacional"
      actions={
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild><Button data-testid="create-survey-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Encuesta</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nueva Encuesta</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Título</Label><Input value={newSurvey.title} onChange={e => setNewSurvey({ ...newSurvey, title: e.target.value })} data-testid="survey-title" /></div>
              <div><Label>Descripción</Label><Textarea rows={2} value={newSurvey.description} onChange={e => setNewSurvey({ ...newSurvey, description: e.target.value })} /></div>
              <div><Label>Tipo</Label>
                <Select value={newSurvey.survey_type} onValueChange={v => setNewSurvey({ ...newSurvey, survey_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="clima">Clima Laboral</SelectItem><SelectItem value="satisfaccion">Satisfacción</SelectItem><SelectItem value="engagement">Engagement</SelectItem><SelectItem value="custom">Personalizada</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preguntas</Label>
                <div className="flex gap-2 mt-1">
                  <Input placeholder="Agregar pregunta..." value={questionInput} onChange={e => setQuestionInput(e.target.value)} onKeyDown={e => e.key === "Enter" && addQuestion()} data-testid="question-input" />
                  <Button variant="outline" onClick={addQuestion}>+</Button>
                </div>
                {newSurvey.questions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newSurvey.questions.map((q, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded p-2">
                        <span className="text-slate-400">{i + 1}.</span>
                        <span className="flex-1">{q.text}</span>
                        <button onClick={() => setNewSurvey(prev => ({ ...prev, questions: prev.questions.filter((_, idx) => idx !== i) }))} className="text-red-400 hover:text-red-600 text-xs">x</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="save-survey-btn">Crear Encuesta</Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      {surveys.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500" data-testid="surveys-empty">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay encuestas</p><p className="text-sm mt-1">Crea tu primera encuesta de clima laboral</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {surveys.map(s => (
            <Card key={s.survey_id} className="card-hover" data-testid={`survey-${s.survey_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    <p className="text-sm text-slate-500">{s.description}</p>
                  </div>
                  <Badge className={s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{s.status}</Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                  <Badge variant="outline">{s.survey_type}</Badge>
                  <span>{s.responses_count || 0} respuestas</span>
                </div>
                <div className="flex gap-2">
                  {s.status === "draft" && <Button size="sm" onClick={() => handlePublish(s.survey_id)} data-testid={`publish-${s.survey_id}`}><Send className="w-3 h-3 mr-1" /> Publicar</Button>}
                  {s.status === "active" && <Button size="sm" variant="outline" onClick={() => handleViewResults(s.survey_id)}><Eye className="w-3 h-3 mr-1" /> Resultados</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Resultados de Encuesta</DialogTitle></DialogHeader>
          {selectedResults && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Total respuestas: {selectedResults.total_responses}</p>
              {Object.entries(selectedResults.questions || {}).map(([qId, data]) => (
                <div key={qId} className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium mb-1">Pregunta {qId}</p>
                  <p className="text-xs text-slate-500">{data.count} respuestas</p>
                </div>
              ))}
              {selectedResults.total_responses === 0 && <p className="text-sm text-slate-400 text-center">Aún no hay respuestas</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Surveys;
